import { useEffect, useState, useMemo } from "react";
import { api, formatTRY, monthLabel } from "@/lib/api";
import { useMonth } from "@/context/MonthContext";
import { Input } from "@/components/ui/input";
import {
  TrendingUp, TrendingDown, Wallet, Calculator, Building2, Layers, Search
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid
} from "recharts";

const CHART_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#3b82f6", "#a855f7", "#06b6d4", "#f43f5e"];

function KPI({ label, value, hint, icon: Icon, tone = "indigo", testid }) {
  const tones = {
    indigo: "from-indigo-500/15 to-transparent text-indigo-300 border-indigo-500/25",
    emerald: "from-emerald-500/15 to-transparent text-emerald-300 border-emerald-500/25",
    crimson: "from-rose-500/15 to-transparent text-rose-300 border-rose-500/25",
    amber: "from-amber-500/15 to-transparent text-amber-300 border-amber-500/25",
    blue: "from-blue-500/15 to-transparent text-blue-300 border-blue-500/25",
  }[tone];
  return (
    <div className="fk-card fk-kpi-ring p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-xs text-slate-400 font-medium uppercase tracking-wider">{label}</div>
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${tones} border flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-4 text-2xl font-semibold fk-mono text-white">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500 fk-mono">{hint}</div>}
    </div>
  );
}

export default function Overview() {
  const { month } = useMonth();
  const [report, setReport] = useState(null);
  const [year, setYear] = useState(null);
  const [cats, setCats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const y = month.slice(0, 4);
        const [r1, r2, r3] = await Promise.all([
          api.get("/reports/monthly", { params: { month } }),
          api.get("/reports/yearly", { params: { year: y } }),
          api.get("/reports/categories", { params: { month } }),
        ]);
        setReport(r1.data); setYear(r2.data); setCats(r3.data);
      } finally { setLoading(false); }
    })();
  }, [month]);

  if (loading || !report) {
    return <div className="text-slate-400">Yükleniyor…</div>;
  }

  const t = report.totals;
  const yearData = (year?.months || []).map((m) => ({
    month: m.month.slice(5),
    "Gelir": m.income,
    "Direkt Gider": m.direct_expense,
    "Ortak Giderler": m.manager_cost,
    "Net Kâr": m.net,
  }));

  return (
    <div className="space-y-6" data-testid="overview-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[.25em] text-slate-500 mb-1">Konsolide Finansal Özet</div>
        <h1 className="text-3xl font-bold tracking-tight fk-heading">
          {monthLabel(month)} — <span className="text-indigo-400">5 Firma Toplamı</span>
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-income" label="Toplam Gelir" value={formatTRY(t.income)} icon={TrendingUp} tone="emerald" />
        <KPI testid="kpi-direct" label="Direkt Giderler" value={formatTRY(t.direct_expense)} icon={TrendingDown} tone="crimson" />
        <KPI testid="kpi-mgr" label="Ortak Gider" value={formatTRY(t.manager_share)}
             hint={`${report.companies_count} firmaya %${report.share_percentage} eşit`} icon={Calculator} tone="amber" />
        <KPI testid="kpi-net" label="Net Kâr" value={formatTRY(t.net)} icon={Wallet}
             tone={t.net >= 0 ? "emerald" : "crimson"} />
      </div>

      {/* Manager share banner */}
      <div className="fk-card p-5 border-amber-500/25 bg-gradient-to-r from-amber-500/5 to-transparent">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Layers className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <div className="text-sm text-white font-semibold">
                Ortak Giderler Otomatik Dağıtımı
              </div>
              <div className="text-xs text-slate-400 mt-1">
                Toplam {formatTRY(report.total_manager_cost)}
                {" "}({formatTRY(report.manager_salary_total)} maaş + {formatTRY(report.manager_adhoc_total)} ortak gider),
                {" "}{report.companies_count} firmaya %{report.share_percentage} eşit paylaştırıldı.
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Firma başına</div>
            <div className="text-xl fk-mono font-semibold text-amber-300">{formatTRY(report.per_company_manager_share)}</div>
          </div>
        </div>
      </div>

      {/* Chart row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="fk-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider font-mono">Firma Karşılaştırması</div>
              <div className="text-sm text-white font-semibold">Gelir vs Gider ({monthLabel(month)})</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={report.companies} margin={{ left: 0, right: 10, top: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="company_name" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ background: "#131b2e", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }}
                formatter={(v) => formatTRY(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="income" name="Gelir" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="direct_expense" name="Direkt Gider" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="manager_share" name="Ortak Payı" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Bar dataKey="net_profit" name="Net Kâr" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="fk-card p-5">
          <div className="mb-2">
            <div className="text-xs text-slate-500 uppercase tracking-wider font-mono">Gider Kategorileri</div>
            <div className="text-sm text-white font-semibold">Direkt Gider Dağılımı</div>
          </div>
          {(cats?.expense || []).length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-slate-500">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={cats.expense} dataKey="amount" nameKey="category" innerRadius={55} outerRadius={95}
                     paddingAngle={3}>
                  {cats.expense.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="transparent" />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#131b2e", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }}
                  formatter={(v) => formatTRY(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Yearly trend */}
      <div className="fk-card p-5">
        <div className="mb-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider font-mono">Yıllık Trend · {month.slice(0, 4)}</div>
          <div className="text-sm text-white font-semibold">Aylık Gelir, Gider ve Net Kâr</div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={yearData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
            <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ background: "#131b2e", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }}
                     formatter={(v) => formatTRY(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="Gelir" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="Direkt Gider" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="Ortak Giderler" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="Net Kâr" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Per-company table */}
      <div className="fk-card p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Building2 className="w-4 h-4 text-indigo-400" />
            <div className="text-sm font-semibold">Firma Bazlı Ay Özeti</div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <Input data-testid="overview-search" value={search} onChange={(e)=>setSearch(e.target.value)}
                   placeholder="Firma ara…"
                   className="h-9 pl-9 w-[220px] bg-slate-900/60 border-slate-700" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[.02] text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Firma</th>
                <th className="text-right px-5 py-3 font-medium">Gelir</th>
                <th className="text-right px-5 py-3 font-medium">Direkt Gider</th>
                <th className="text-right px-5 py-3 font-medium">Ortak Payı</th>
                <th className="text-right px-5 py-3 font-medium">Net Kâr</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {report.companies
                .filter((c) => {
                  const q = search.trim().toLocaleLowerCase("tr");
                  return !q || (c.company_name || "").toLocaleLowerCase("tr").includes(q);
                })
                .map((c) => (
                <tr key={c.company_id} data-testid={`company-row-${c.company_id}`}
                    className="hover:bg-white/[.02] transition">
                  <td className="px-5 py-3 font-medium text-white">{c.company_name}</td>
                  <td className="px-5 py-3 text-right fk-mono text-emerald-400">{formatTRY(c.income)}</td>
                  <td className="px-5 py-3 text-right fk-mono text-rose-400">{formatTRY(c.direct_expense)}</td>
                  <td className="px-5 py-3 text-right fk-mono text-amber-400">{formatTRY(c.manager_share)}</td>
                  <td className={`px-5 py-3 text-right fk-mono font-semibold ${c.net_profit >= 0 ? "text-indigo-300" : "text-rose-300"}`}>
                    {formatTRY(c.net_profit)}
                  </td>
                </tr>
              ))}
              {report.companies.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">
                  Henüz firma eklenmemiş. "Firmalar" sayfasından ekleyin.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
